/* O que a gaveta de Notas emitidas e a página da nota têm em comum: o DANFSe de verdade
   (PDF gerado no servidor, com marca d'água quando cancelada ou substituída), o histórico
   montado dos campos da emissão e as ações de baixar, cancelar, substituir e duplicar. */
import React, { useEffect, useState } from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { downloadBlob } from '../../../../../hooks/useEstatuto';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { INfseEmissao } from '../../../../../types/nfseEmissao';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { duplicarPath, editarPath } from './constants';
import { island } from './NfseIsland';
import { C, dataBR, Eyebrow, horaBR } from './ui';

export function DanfsePreview({
  emissao,
  altura = 520,
}: {
  emissao: INfseEmissao;
  altura?: number;
}) {
  const { downloadArquivo } = useNfseEmissao();
  const temPdf = ['autorizada', 'cancelada', 'substituida'].includes(emissao.status);
  const pdf = useQuery({
    // O status entra na chave: cancelar troca o PDF (marca d'água).
    queryKey: ['nfse-pdf', emissao.id, emissao.status],
    queryFn: () => downloadArquivo(emissao.id, 'pdf'),
    enabled: temPdf,
    staleTime: 10 * 60 * 1000,
  });
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!pdf.data) return undefined;
    const u = URL.createObjectURL(pdf.data);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [pdf.data]);

  if (!temPdf) {
    return (
      <Text fontSize="13px" color={C.subtle}>
        O DANFSe sai junto com a autorização da Sefin.
      </Text>
    );
  }
  return (
    <Box
      position="relative"
      h={`${altura}px`}
      borderRadius="14px"
      overflow="hidden"
      bg="white"
      boxShadow="0 1px 2px rgba(15,23,42,.06), 0 12px 30px -12px rgba(15,23,42,.25)"
    >
      {url ? (
        <iframe
          title={`DANFSe da NFS-e ${emissao.n_nfse ?? ''}`}
          src={`${url}#toolbar=0&navpanes=0&view=FitH`}
          style={{ width: '100%', height: '100%', border: 0 }}
        />
      ) : (
        <Flex h="full" align="center" justify="center" direction="column" gap={2}>
          {pdf.isError ? (
            <Text fontSize="13px" color={C.danger}>
              Não foi possível carregar o DANFSe.
            </Text>
          ) : (
            <>
              <Spinner color={C.brand700} />
              <Text fontSize="12px" color={C.subtle}>
                Carregando o DANFSe…
              </Text>
            </>
          )}
        </Flex>
      )}
    </Box>
  );
}

interface Evento {
  titulo: string;
  quando?: string | null;
  quem?: string | null;
  cor?: string;
}

export function Historico({ emissao: e }: { emissao: INfseEmissao }) {
  const eventos: Evento[] = [{ titulo: 'DPS criada', quando: e.criado_em, quem: e.usuario_nome }];
  if (e.substitui_n_nfse) eventos.push({ titulo: `Substitui a NFS-e nº ${e.substitui_n_nfse}` });
  if (e.status === 'rejeitada') {
    const codigo = e.erros?.[0]?.codigo;
    eventos.push({
      titulo: `Rejeitada${codigo ? ` (${codigo})` : ''}`,
      quem: 'Sefin',
      cor: C.danger,
    });
  }
  if (e.dh_processamento) {
    eventos.push({
      titulo: `Autorizada · NFS-e nº ${e.n_nfse}`,
      quando: e.dh_processamento,
      quem: 'Sefin',
    });
  }
  if (e.email_enviado_em) {
    eventos.push({
      titulo: 'E-mail enviado',
      quando: e.email_enviado_em,
      quem: e.email_enviado_para,
    });
  }
  if (e.cancelado_em) {
    eventos.push({
      titulo: 'Cancelada',
      quando: e.cancelado_em,
      quem: "evento aceito · marca d'água no PDF",
      cor: C.danger,
    });
  }
  if (e.status === 'substituida') {
    eventos.push({
      titulo: 'Substituída',
      quem: e.substituida_por_n_nfse
        ? `pela NFS-e nº ${e.substituida_por_n_nfse}`
        : 'pela nota nova',
      cor: C.brand700,
    });
  }
  return (
    <Box>
      <Eyebrow>Histórico</Eyebrow>
      <Flex as="ol" direction="column" gap={3} mt={2.5} listStyleType="none">
        {eventos.map(ev => (
          <Box as="li" key={ev.titulo} fontSize="12px">
            <Text fontWeight={700} color={ev.cor ?? C.ink}>
              {ev.titulo}
            </Text>
            <Text color={C.subtle}>
              {[ev.quando ? `${dataBR(ev.quando)} ${horaBR(ev.quando)}` : null, ev.quem]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </Box>
        ))}
      </Flex>
    </Box>
  );
}

export function useAcoesNota() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useNfseEmissao();
  const [baixando, setBaixando] = useState<string | null>(null);
  const [cancelando, setCancelando] = useState<INfseEmissao | null>(null);

  const atualizar = (id?: number) => {
    if (id) queryClient.invalidateQueries({ queryKey: ['nfse-emissao', id] });
    queryClient.invalidateQueries({ queryKey: ['nfse-emissoes'] });
    queryClient.invalidateQueries({ queryKey: ['nfse-contagem'] });
    queryClient.invalidateQueries({ queryKey: ['nfse-totalizadores'] });
    queryClient.invalidateQueries({ queryKey: ['nfse-painel'] });
  };

  const baixar = async (nota: INfseEmissao, tipo: 'pdf' | 'xml') => {
    setBaixando(`${nota.id}-${tipo}`);
    try {
      const blob = await api.downloadArquivo(nota.id, tipo);
      downloadBlob(blob, `NFSe-${nota.n_nfse ?? nota.id}-${nota.chave_acesso ?? ''}.${tipo}`);
      island.notify(`${tipo === 'pdf' ? 'DANFSe' : 'XML'} baixado`, {
        tone: 'ok',
        meta: `NFS-e-${nota.n_nfse}.${tipo}`,
      });
    } catch (err) {
      island.notify(
        extractApiErrorMessage(err, `Não foi possível baixar o ${tipo.toUpperCase()}.`),
        {
          tone: 'bad',
        },
      );
    } finally {
      setBaixando(null);
    }
  };

  const cancelar = useMutation({
    mutationFn: ({
      nota,
      payload,
    }: {
      nota: INfseEmissao;
      payload: { motivo: string; justificativa: string };
    }) => api.cancelarEmissao(nota.id, payload),
    onMutate: ({ nota }) =>
      island.live('Pedindo o cancelamento à Sefin', `NFS-e nº ${nota.n_nfse} · evento e101101`),
    onSuccess: nota => {
      island.notify(`NFS-e nº ${nota.n_nfse} cancelada`, { tone: 'info', meta: 'evento aceito' });
      setCancelando(null);
      atualizar(nota.id);
    },
    onError: err =>
      island.notify(extractApiErrorMessage(err, 'A Sefin não aceitou o cancelamento.'), {
        tone: 'bad',
        ms: 6000,
      }),
  });

  const substituir = useMutation({
    mutationFn: (nota: INfseEmissao) => api.substituirEmissao(nota.id),
    onSuccess: nova => {
      atualizar();
      island.notify('Nota substituta criada', { tone: 'info', meta: 'confira e emita' });
      navigate(editarPath(nova.id));
    },
    onError: err =>
      island.notify(extractApiErrorMessage(err, 'Não foi possível criar a nota substituta.'), {
        tone: 'bad',
      }),
  });

  return {
    baixar,
    baixando,
    cancelando,
    abrirCancelamento: setCancelando,
    fecharCancelamento: () => setCancelando(null),
    cancelar,
    substituir,
    duplicar: (nota: INfseEmissao) => navigate(duplicarPath(nota.id)),
  };
}
